import React, { useRef, useState, useEffect } from "react";
import styled from "styled-components";
import { Theme } from "../atoms/theme";
import { Note } from "../../types/Note";
import {
  PencilIcon,
  StarIcon,
  BanIcon,
  CheckIcon,
  ClipboardCopyIcon,
} from "@heroicons/react/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/react/solid";
import useToast from "../../hooks/useToast";

/* ── Compact preview (collapsed list) ── */

const PreviewRow = styled.div<{
  isHighlighted: boolean;
  isBanned: boolean;
  isClientNote: boolean;
}>`
  padding: 0.5rem;
  border-left: 3px solid
    ${({ isHighlighted, isClientNote }) =>
      isHighlighted
        ? Theme.colors.warning_000
        : isClientNote
        ? Theme.colors.primaries_100
        : "transparent"};
  padding-left: ${({ isHighlighted, isClientNote }) =>
    isHighlighted || isClientNote ? "8px" : "0.5rem"};
  opacity: ${({ isBanned }) => (isBanned ? 0.45 : 1)};
  background: ${({ isHighlighted, isClientNote }) =>
    isHighlighted
      ? "#FFFDE7"
      : isClientNote
      ? `${Theme.colors.primaries_300}40`
      : "transparent"};
  border-radius: ${({ isHighlighted, isClientNote }) =>
    isHighlighted || isClientNote ? "4px" : "0"};
`;

const PreviewBody = styled.div<{ isBanned: boolean; isHighlighted: boolean }>`
  font-size: 13px;
  line-height: 1.45;
  color: ${Theme.colors.neutrals_000};
  white-space: pre-wrap;
  word-break: break-word;
  text-decoration: ${({ isBanned }) => (isBanned ? "line-through" : "none")};
  ${({ isHighlighted }) =>
    isHighlighted
      ? ""
      : `
    display: -webkit-box;
    -webkit-line-clamp: 8;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${Theme.colors.neutrals_300};
  margin: 0;
`;

/* ── Full note (expanded list) ── */

const FullRow = styled.div<{
  isHighlighted: boolean;
  isBanned: boolean;
  isSelected: boolean;
  isClientNote: boolean;
}>`
  position: relative;
  padding: 0.625rem;
  border-left: 3px solid
    ${({ isHighlighted, isClientNote }) =>
      isHighlighted
        ? Theme.colors.warning_000
        : isClientNote
        ? Theme.colors.primaries_100
        : "transparent"};
  padding-left: ${({ isHighlighted, isClientNote }) =>
    isHighlighted || isClientNote ? "10px" : "0.625rem"};
  opacity: ${({ isBanned }) => (isBanned ? 0.45 : 1)};
  background: ${({ isSelected, isHighlighted, isClientNote }) =>
    isSelected
      ? Theme.colors.primaries_300
      : isHighlighted
      ? "#FFFDE7"
      : isClientNote
      ? `${Theme.colors.primaries_300}40`
      : "transparent"};
  border-radius: ${({ isSelected, isHighlighted, isClientNote }) =>
    isSelected || isHighlighted || isClientNote ? "4px" : "0"};
  &:hover .note-actions {
    opacity: 1;
  }
`;

const FullMeta = styled.div`
  font-size: 11px;
  color: ${Theme.colors.gray_200};
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SelectCheckbox = styled.input`
  cursor: pointer;
  accent-color: ${Theme.colors.primaries_000};
`;

const FullBody = styled.div<{ isBanned: boolean }>`
  font-size: 13px;
  line-height: 1.55;
  color: ${Theme.colors.neutrals_000};
  white-space: pre-wrap;
  word-break: break-word;
  text-decoration: ${({ isBanned }) => (isBanned ? "line-through" : "none")};
  user-select: text;
`;

const HoverActions = styled.div`
  position: absolute;
  top: 4px;
  right: 0;
  display: flex;
  gap: 1px;
  opacity: 0;
  transition: opacity 0.12s;
  background: ${Theme.colors.neutrals_500};
  border: 1px solid ${Theme.colors.neutrals_300};
  border-radius: 4px;
  padding: 1px;
`;

const IconBtn = styled.button.attrs({ type: "button" })<{ active?: boolean }>`
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ active }) =>
    active ? Theme.colors.primaries_000 : Theme.colors.gray_200};
  &:hover {
    background: ${Theme.colors.neutrals_400};
    color: ${Theme.colors.primaries_000};
  }
`;

/* ── Inline edit ── */

const EditArea = styled.textarea`
  width: 100%;
  min-height: 52px;
  padding: 0.4rem;
  border: 1px solid ${Theme.colors.primaries_100};
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  resize: vertical;
  font-family: inherit;
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Theme.colors.primaries_300};
  }
`;

const EditBtns = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 4px;
  justify-content: flex-end;
`;

const SmallBtn = styled.button.attrs({ type: "button" })<{ primary?: boolean }>`
  background: ${({ primary }) =>
    primary ? Theme.colors.primaries_000 : Theme.colors.neutrals_500};
  color: ${({ primary }) => (primary ? "#fff" : Theme.colors.gray_200)};
  border: 1px solid
    ${({ primary }) =>
      primary ? Theme.colors.primaries_000 : Theme.colors.neutrals_300};
  cursor: pointer;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 3px;
  &:hover {
    opacity: 0.85;
  }
`;

/* ── Client badge ── */

const ClientBadge = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 10px;
  font-weight: 700;
  color: ${Theme.colors.primaries_000};
  background: ${Theme.colors.primaries_300};
  border-radius: 3px;
  padding: 1px 5px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.4;
`;

/* ── Edited badge ── */

const EditedBadge = styled.span`
  font-size: 10px;
  font-style: italic;
  color: ${Theme.colors.gray_200};
`;

/* ── Helpers ── */

function formatMeta(dateStr: string, createdByName: string | null) {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${createdByName || "Unknown"} · ${date}, ${time}`;
}

function isEdited(note: Note): boolean {
  if (!note.updatedAt || !note.createdAt) return false;
  const diff = Math.abs(
    new Date(note.updatedAt).getTime() - new Date(note.createdAt).getTime()
  );
  return diff > 2000; // more than 2 seconds apart = was edited
}

/* ── Props ── */

interface NoteCardProps {
  note: Note;
  compact?: boolean;
  selected?: boolean;
  isClientNote?: boolean;
  onSelect?: (noteId: string, selected: boolean) => void;
  onSaveEdit?: (noteId: string, body: string) => void;
  onToggleHighlight?: (note: Note) => void;
  onToggleBan?: (note: Note) => void;
  showDivider?: boolean;
  readOnly?: boolean;
}

export const NoteCard = ({
  note,
  compact = false,
  selected = false,
  isClientNote = false,
  onSelect,
  onSaveEdit,
  onToggleHighlight,
  onToggleBan,
  showDivider = true,
  readOnly = false,
}: NoteCardProps) => {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(note.body);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const toast = useToast();

  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      taRef.current.selectionStart = taRef.current.value.length;
    }
  }, [editing]);

  const meta = formatMeta(note.createdAt, note.createdByName);

  const copyText = () => {
    const text = `${meta}\n${note.body}`;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const startEdit = () => {
    setEditVal(note.body);
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
  };
  const saveEdit = () => {
    if (editVal.trim() && editVal.trim() !== note.body) {
      onSaveEdit?.(note.id, editVal.trim());
    }
    setEditing(false);
  };

  const hasAnyAction =
    !readOnly && !!(onSaveEdit || onToggleHighlight || onToggleBan);

  /* ── Compact preview ── */
  if (compact) {
    return (
      <>
        <PreviewRow
          isHighlighted={note.isHighlighted}
          isBanned={note.isBanned}
          isClientNote={isClientNote}
        >
          <PreviewBody
            isBanned={note.isBanned}
            isHighlighted={note.isHighlighted}
          >
            {note.body}
          </PreviewBody>
        </PreviewRow>
        {showDivider && <Divider />}
      </>
    );
  }

  /* ── Full note ── */
  return (
    <>
      <FullRow
        isHighlighted={note.isHighlighted}
        isBanned={note.isBanned}
        isSelected={selected}
        isClientNote={isClientNote}
      >
        {hasAnyAction && (
          <HoverActions className="note-actions">
            {onToggleHighlight && (
              <>
                <IconBtn onClick={copyText} title="Copy text">
                  <ClipboardCopyIcon width={14} />
                </IconBtn>
                <IconBtn
                  onClick={() => onToggleHighlight(note)}
                  active={note.isHighlighted}
                  title={note.isHighlighted ? "Remove highlight" : "Highlight"}
                >
                  {note.isHighlighted ? (
                    <StarSolidIcon width={14} />
                  ) : (
                    <StarIcon width={14} />
                  )}
                </IconBtn>
              </>
            )}
            {onToggleBan && (
              <IconBtn
                onClick={() => onToggleBan(note)}
                active={note.isBanned}
                title={note.isBanned ? "Restore" : "Strike out"}
              >
                <BanIcon width={14} />
              </IconBtn>
            )}
            {onSaveEdit && (
              <IconBtn onClick={startEdit} title="Edit">
                <PencilIcon width={14} />
              </IconBtn>
            )}
          </HoverActions>
        )}

        <FullMeta>
          {onSelect && (
            <SelectCheckbox
              type="checkbox"
              checked={selected}
              onChange={(e) => onSelect(note.id, e.target.checked)}
              title="Select note"
            />
          )}
          {meta}
          {isEdited(note) && <EditedBadge>(edited)</EditedBadge>}
          {isClientNote && <ClientBadge>Client</ClientBadge>}
        </FullMeta>

        {editing ? (
          <>
            <EditArea
              ref={taRef}
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") cancelEdit();
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveEdit();
              }}
            />
            <EditBtns>
              <SmallBtn onClick={cancelEdit}>Cancel</SmallBtn>
              <SmallBtn primary onClick={saveEdit}>
                <CheckIcon width={12} /> Save
              </SmallBtn>
            </EditBtns>
          </>
        ) : (
          <FullBody isBanned={note.isBanned}>{note.body}</FullBody>
        )}
      </FullRow>
      {showDivider && <Divider />}
    </>
  );
};
